import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScheduleEditor } from '@/components/ui/schedule-editor';
import DashboardLayout from '@/app/dashboard/layout';
import { Calendar, Clock, Plus, MoreVertical } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import { useOrganisation } from '@/App';
import {
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  setDefaultSchedule,
  duplicateSchedule,
} from '@/client/sdk.gen';
import type {
  DayOfWeek,
  DayInterval,
  Schedule as ScheduleType,
} from '@/client/types.gen';

const DAYS_OF_WEEK: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

export default function AvailabilityPage() {
  const { toast } = useToast();
  const { organisationId } = useOrganisation();
  const [schedules, setSchedules] = useState<ScheduleType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<ScheduleType | null>(
    null
  );

  // Schedule editor state
  const [timezone, setTimezone] = useState<string>('UTC');
  const [scheduleName, setScheduleName] = useState<string>('Working Hours');
  const [scheduleConfig, setScheduleConfig] = useState<
    Record<DayOfWeek, DayInterval[]>
  >(
    DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = [
        {
          start_time: '09:00',
          end_time: '17:00',
        },
      ];
      return acc;
    }, {} as Record<DayOfWeek, DayInterval[]>)
  );
  const [availability, setAvailability] = useState<Record<DayOfWeek, boolean>>(
    DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = day !== 'SATURDAY' && day !== 'SUNDAY';
      return acc;
    }, {} as Record<DayOfWeek, boolean>)
  );

  const fetchSchedules = async () => {
    setIsLoading(true);
    try {
      const response = await getSchedules();

      if (response.data) {
        setSchedules(response.data.schedules);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
      toast({
        title: 'Error',
        description: 'Failed to load schedules',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [organisationId]);

  const resetScheduleForm = () => {
    setTimezone('UTC');
    setScheduleName('Working Hours');
    setScheduleConfig(
      DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = [
          {
            start_time: '09:00',
            end_time: '17:00',
          },
        ];
        return acc;
      }, {} as Record<DayOfWeek, DayInterval[]>)
    );
    setAvailability(
      DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = day !== 'SATURDAY' && day !== 'SUNDAY';
        return acc;
      }, {} as Record<DayOfWeek, boolean>)
    );
  };

  const handleEditSchedule = (schedule: ScheduleType) => {
    setIsEditing(true);
    setCurrentSchedule(schedule);

    // Set timezone and name
    setTimezone(schedule.time_zone);
    setScheduleName(schedule.name || 'Working Hours');

    // Set schedule config and availability
    const newScheduleConfig: Record<DayOfWeek, DayInterval[]> = {} as Record<
      DayOfWeek,
      DayInterval[]
    >;
    const newAvailability: Record<DayOfWeek, boolean> = {} as Record<
      DayOfWeek,
      boolean
    >;

    schedule.schedule_config.week_of_day_configs.forEach((config) => {
      newScheduleConfig[config.day_of_week] = config.intervals;
      newAvailability[config.day_of_week] = config.enabled;
    });

    setScheduleConfig(newScheduleConfig);
    setAvailability(newAvailability);

    setIsDialogOpen(true);
  };

  const handleCreateNewSchedule = () => {
    setIsEditing(false);
    setCurrentSchedule(null);
    resetScheduleForm();
    setIsDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const scheduleConfigData = {
        week_of_day_configs: DAYS_OF_WEEK.map((day) => ({
          day_of_week: day,
          intervals: scheduleConfig[day],
          enabled: availability[day],
        })),
      };

      if (isEditing && currentSchedule) {
        // Update existing schedule
        await updateSchedule({
          path: { schedule_id: currentSchedule.id },
          body: {
            name: scheduleName,
            time_zone: timezone,
            schedule_config: scheduleConfigData,
            default_schedule: currentSchedule.default_schedule,
          },
        });

        toast({
          title: 'Success',
          description: 'Schedule updated successfully',
        });
      } else {
        // Create new schedule
        await createSchedule({
          body: {
            name: scheduleName,
            time_zone: timezone,
            schedule_config: scheduleConfigData,
            default_schedule: schedules.length === 0, // Make default if first schedule
          },
        });

        toast({
          title: 'Success',
          description: 'Schedule created successfully',
        });
      }

      // Refresh schedules
      await fetchSchedules();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to save schedule',
        variant: 'destructive',
      });
    }
  };

  const handleSetDefault = async (scheduleId: string) => {
    try {
      await setDefaultSchedule({
        path: { schedule_id: scheduleId },
      });

      toast({
        title: 'Success',
        description: 'Default schedule updated',
      });

      await fetchSchedules();
    } catch (error) {
      console.error('Failed to set default schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to set default schedule',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    try {
      await deleteSchedule({
        path: { schedule_id: scheduleId },
      });

      toast({
        title: 'Success',
        description: 'Schedule deleted successfully',
      });

      await fetchSchedules();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete schedule',
        variant: 'destructive',
      });
    }
  };

  const handleDuplicateSchedule = async (schedule: ScheduleType) => {
    try {
      // Use the duplicateSchedule SDK function
      await duplicateSchedule({
        path: { schedule_id: schedule.id },
      });

      toast({
        title: 'Success',
        description: 'Schedule duplicated successfully',
      });

      // Refresh schedules
      await fetchSchedules();
    } catch (error) {
      console.error('Failed to duplicate schedule:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate schedule',
        variant: 'destructive',
      });
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const formatScheduleDisplayTime = (schedule: ScheduleType) => {
    // Group days by their intervals
    const daysBySchedule: Record<string, DayOfWeek[]> = {};

    schedule.schedule_config.week_of_day_configs.forEach((config) => {
      if (!config.enabled) return; // Skip disabled days

      // Create a key based on intervals
      const key = JSON.stringify(config.intervals);
      if (!daysBySchedule[key]) {
        daysBySchedule[key] = [];
      }
      daysBySchedule[key].push(config.day_of_week);
    });

    // Format the groups
    const formattedGroups = Object.entries(daysBySchedule).map(
      ([intervalKey, days]) => {
        const intervals = JSON.parse(intervalKey) as DayInterval[];

        // Format days (e.g., "Mon-Fri" or "Mon, Wed")
        const formattedDays = formatDayGroup(days);

        // Format time intervals
        const formattedIntervals = intervals
          .map(
            (interval) =>
              `${formatTime(interval.start_time)} - ${formatTime(
                interval.end_time
              )}`
          )
          .join(', ');

        return `${formattedDays} ${formattedIntervals}`;
      }
    );

    return formattedGroups.join(' • ');
  };

  const formatDayGroup = (days: DayOfWeek[]) => {
    // Sort days according to the week order
    const sortedDays = [...days].sort(
      (a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b)
    );

    // Abbreviate day names
    const dayAbbreviations: Record<DayOfWeek, string> = {
      MONDAY: 'Mon',
      TUESDAY: 'Tue',
      WEDNESDAY: 'Wed',
      THURSDAY: 'Thu',
      FRIDAY: 'Fri',
      SATURDAY: 'Sat',
      SUNDAY: 'Sun',
    };

    // Find consecutive day ranges
    const ranges: Array<{ start: number; end: number }> = [];
    let rangeStart = 0;

    for (let i = 1; i <= sortedDays.length; i++) {
      const prevIndex = DAYS_OF_WEEK.indexOf(sortedDays[i - 1]);
      const currIndex =
        i < sortedDays.length ? DAYS_OF_WEEK.indexOf(sortedDays[i]) : -1;

      if (i === sortedDays.length || currIndex !== prevIndex + 1) {
        // End of a range
        ranges.push({
          start: rangeStart,
          end: i - 1,
        });
        rangeStart = i;
      }
    }

    // Format ranges
    return ranges
      .map((range) => {
        const startDay = sortedDays[range.start];
        const endDay = sortedDays[range.end];

        if (startDay === endDay) {
          return dayAbbreviations[startDay];
        } else if (range.end - range.start === 1) {
          return `${dayAbbreviations[startDay]}, ${dayAbbreviations[endDay]}`;
        } else {
          return `${dayAbbreviations[startDay]}-${dayAbbreviations[endDay]}`;
        }
      })
      .join(', ');
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Home', href: '/dashboard' },
        { label: 'Availability' },
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Availability</h1>
            <p className="text-muted-foreground">
              Configure times when you are available for bookings.
            </p>
          </div>
          <Button onClick={handleCreateNewSchedule}>
            <Plus className="mr-2 h-4 w-4" />
            New
          </Button>
        </div>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p className="text-muted-foreground">Loading schedules...</p>
            </div>
          ) : schedules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 border rounded-lg">
              <Clock className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No schedules yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first availability schedule
              </p>
              <Button onClick={handleCreateNewSchedule}>
                <Plus className="mr-2 h-4 w-4" />
                Create Schedule
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {schedules.map((schedule) => (
                <div
                  key={schedule.id}
                  className="flex items-center justify-between p-4 rounded-lg border"
                >
                  <div className="flex items-center">
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${
                        schedule.default_schedule
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted/20 text-muted-foreground'
                      }`}
                    >
                      <Calendar className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="flex items-center">
                        <h3 className="font-medium">
                          {schedule.name || 'Working Hours'}
                        </h3>
                        {schedule.default_schedule && (
                          <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm text-muted-foreground">
                          {formatScheduleDisplayTime(schedule)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Timezone: {schedule.time_zone}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditSchedule(schedule)}
                    >
                      Edit
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleDuplicateSchedule(schedule)}
                        >
                          Duplicate
                        </DropdownMenuItem>
                        {!schedule.default_schedule && (
                          <DropdownMenuItem
                            onClick={() => handleSetDefault(schedule.id)}
                          >
                            Set as default
                          </DropdownMenuItem>
                        )}
                        {!schedule.default_schedule && (
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDeleteSchedule(schedule.id)}
                          >
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Schedule' : 'Create New Schedule'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name</label>
              <input
                type="text"
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="e.g., Working Hours"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Timezone</label>
              <TimezoneSelect value={timezone} onValueChange={setTimezone} />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Schedule</label>
              <ScheduleEditor
                schedule={scheduleConfig}
                availability={availability}
                onScheduleChange={setScheduleConfig}
                onAvailabilityChange={setAvailability}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSchedule}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
